import { Response, TranscriptionControllerApi } from "@/services";
import { getConfigObject, getXtenantId } from "@/utils/utils";
import { useQuery } from "@tanstack/react-query";

/** Domain query key for the scribe's patient-context fetch. */
export const PATIENT_SCRIBE_CONTEXT_QUERY_KEY = "patient-scribe-context";

/** Endpoint identifier used when logging the call. */
export const PATIENT_SCRIBE_CONTEXT_API_NAME =
  "GET /api/master/transcription/patient-context/{patientUuid}";

/**
 * Console labels for the call. The envelope's `message` is always null on this
 * endpoint — the chart context lives in `data`, so that is what gets logged.
 */
export const PATIENT_SCRIBE_CONTEXT_LOG = {
  SUCCESS: `${PATIENT_SCRIBE_CONTEXT_API_NAME} hit properly. Patient context:`,
  FAILURE: `${PATIENT_SCRIBE_CONTEXT_API_NAME} failed. Error:`,
} as const;

interface Props {
  /** Patient whose chart context the scribe should load. */
  patientUuid?: string;
  /**
   * Gate for the call. The scribe passes the live socket state here so the
   * request is only fired once the audio connection is actually open.
   */
  enabled?: boolean;
}

/**
 * GET /api/master/transcription/patient-context/{patientUuid}
 *
 * Returns the patient chart context (plus the verbatim prompt text) the scribe
 * hands to the model. Disabled until `enabled` is true, so nothing is requested
 * before the scribe session is connected. The outcome is logged here, next to
 * the request, so consumers only have to call the hook.
 *
 * @author [Atish Bagate]
 */
export const usePatientScribeContext = ({ patientUuid, enabled }: Props) =>
  useQuery<Response>({
    queryKey: [PATIENT_SCRIBE_CONTEXT_QUERY_KEY, patientUuid],
    enabled: !!patientUuid && !!enabled,
    refetchOnWindowFocus: false,
    retry: false,
    queryFn: async () => {
      try {
        const response = await new TranscriptionControllerApi(
          getConfigObject()
        ).getPatientScribeContext(patientUuid as string, getXtenantId());

        return response?.data;
      } catch (error) {
        console.error(
          `${PATIENT_SCRIBE_CONTEXT_LOG.FAILURE} ${JSON.stringify(error)}`
        );
        throw error;
      }
    },
  });
